import React, { useState, useMemo } from "react";
import { Box, Grid, useTheme, Typography, Divider } from "@mui/material";
import {
  AccountBalance as AccountBalanceIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  MonetizationOn as MonetizationOnIcon,
  Store as StoreIcon,
  Receipt as ReceiptIcon,
  Schedule as ScheduleIcon,
  Check as CheckIcon,
} from "@mui/icons-material";
import Chart from "react-apexcharts";

// Components
import { DashboardHeader } from "./components/DashboardHeader";
import { MetricCard } from "./components/MetricCard";
import { GaugeChart } from "./components/GaugeChart";
import { ChartCard } from "./components/ChartCard";

// Data
import { dashboardData } from "../../mock.data";

/**
 * Advanced Accounting Dashboard Page
 *
 * Features:
 * - Enterprise-level financial analytics
 * - Real-time KPI monitoring
 * - Advanced chart visualizations
 * - Responsive design with professional UX
 * - Interactive settings and customization
 * - Health score monitoring
 * - Liquidity and profitability tracking
 */
export const AccountingDashboardPage: React.FC = () => {
  const theme = useTheme();
  const [selectedPeriod, setSelectedPeriod] = useState("this_month");
  const [isCustomizing, setIsCustomizing] = useState(false);

  // Get current metrics
  const currentMetrics = dashboardData.metrics[0]; // Latest month
  const previousMetrics = dashboardData.metrics[1]; // Previous month

  // Calculate changes
  const revenueChange =
    ((currentMetrics.totalRevenue - previousMetrics.totalRevenue) /
      previousMetrics.totalRevenue) *
    100;
  const expenseChange =
    ((currentMetrics.totalExpenses - previousMetrics.totalExpenses) /
      previousMetrics.totalExpenses) *
    100;
  const profitChange =
    ((currentMetrics.netProfit - previousMetrics.netProfit) /
      previousMetrics.netProfit) *
    100;
  const cashFlowChange =
    ((currentMetrics.cashFlow - previousMetrics.cashFlow) /
      previousMetrics.cashFlow) *
    100;

  // Chart configurations
  const monthlyTrendOptions = {
    chart: {
      type: "area" as const,
      toolbar: { show: false },
      sparkline: { enabled: false },
    },
    dataLabels: { enabled: false },
    stroke: { curve: "smooth" as const, width: 2 },
    xaxis: {
      categories: dashboardData.monthlyTrends.map((t) => t.month),
      labels: { style: { colors: theme.palette.text.secondary } },
    },
    yaxis: {
      labels: {
        style: { colors: theme.palette.text.secondary },
        formatter: (val: number) => `${(val / 1000).toFixed(0)}K`,
      },
    },
    grid: {
      borderColor: theme.palette.divider,
      strokeDashArray: 3,
    },
    colors: [
      theme.palette.primary.main,
      theme.palette.success.main,
      theme.palette.info.main,
    ],
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.3,
        opacityTo: 0.1,
      },
    },
    legend: {
      position: "top" as const,
      labels: { colors: theme.palette.text.primary },
    },
  };

  const monthlyTrendSeries = [
    {
      name: "Revenue",
      data: dashboardData.monthlyTrends.map((t) => t.revenue),
    },
    {
      name: "Profit",
      data: dashboardData.monthlyTrends.map((t) => t.profit),
    },
    {
      name: "Cash Flow",
      data: dashboardData.monthlyTrends.map((t) => t.cashFlow),
    },
  ];

  const branchPerformanceOptions = {
    chart: {
      type: "bar" as const,
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "60%",
      },
    },
    dataLabels: { enabled: false },
    xaxis: {
      categories: dashboardData.branchPerformance.map((b) =>
        b.branchName.replace(" Branch", "")
      ),
      labels: { style: { colors: theme.palette.text.secondary } },
    },
    yaxis: {
      labels: {
        style: { colors: theme.palette.text.secondary },
        formatter: (val: number) => `${(val / 1000).toFixed(0)}K`,
      },
    },
    grid: {
      borderColor: theme.palette.divider,
      strokeDashArray: 3,
    },
    colors: [theme.palette.primary.main, theme.palette.warning.main],
    legend: {
      position: "top" as const,
      labels: { colors: theme.palette.text.primary },
    },
  };

  const branchPerformanceSeries = [
    {
      name: "Revenue",
      data: dashboardData.branchPerformance.map((b) => b.revenue),
    },
    {
      name: "Expenses",
      data: dashboardData.branchPerformance.map((b) => b.expenses),
    },
  ];

  const expenseBreakdownOptions = {
    chart: {
      type: "donut" as const,
      toolbar: { show: false },
    },
    labels: dashboardData.expenseBreakdown.map((e) => e.category),
    colors: [
      theme.palette.primary.main,
      theme.palette.success.main,
      theme.palette.warning.main,
      theme.palette.error.main,
      theme.palette.info.main,
    ],
    legend: {
      position: "bottom" as const,
      labels: { colors: theme.palette.text.primary },
    },
    plotOptions: {
      pie: {
        donut: {
          size: "70%",
          labels: {
            show: true,
            total: {
              show: true,
              label: "Total Expenses",
              formatter: () =>
                `${(currentMetrics.totalExpenses / 1000).toFixed(0)}K BHD`,
            },
          },
        },
      },
    },
    dataLabels: {
      formatter: (val: number) => `${val.toFixed(1)}%`,
    },
  };

  const expenseBreakdownSeries = dashboardData.expenseBreakdown.map(
    (e) => e.amount
  );

  const cashFlowOptions = {
    chart: {
      type: "line" as const,
      toolbar: { show: false },
    },
    stroke: { curve: "smooth" as const, width: 3 },
    xaxis: {
      categories: dashboardData.cashFlowData.map((c) =>
        new Date(c.date).toLocaleDateString("en-US", { month: "short" })
      ),
      labels: { style: { colors: theme.palette.text.secondary } },
    },
    yaxis: {
      labels: {
        style: { colors: theme.palette.text.secondary },
        formatter: (val: number) => `${(val / 1000).toFixed(0)}K`,
      },
    },
    grid: {
      borderColor: theme.palette.divider,
      strokeDashArray: 3,
    },
    colors: [
      theme.palette.success.main,
      theme.palette.error.main,
      theme.palette.warning.main,
    ],
    legend: {
      position: "top" as const,
      labels: { colors: theme.palette.text.primary },
    },
  };

  const cashFlowSeries = [
    {
      name: "Operating",
      data: dashboardData.cashFlowData.map((c) => c.operating),
    },
    {
      name: "Investing",
      data: dashboardData.cashFlowData.map((c) => c.investing),
    },
    {
      name: "Financing",
      data: dashboardData.cashFlowData.map((c) => c.financing),
    },
  ];

  // Additional chart configurations for new charts
  const budgetVsActualOptions = {
    chart: {
      type: "bar" as const,
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "60%",
        grouped: true,
      },
    },
    dataLabels: { enabled: false },
    xaxis: {
      categories: ["Q1", "Q2", "Q3", "Q4"],
      labels: { style: { colors: theme.palette.text.secondary } },
    },
    yaxis: {
      labels: {
        style: { colors: theme.palette.text.secondary },
        formatter: (val: number) => `${(val / 1000).toFixed(0)}K`,
      },
    },
    grid: {
      borderColor: theme.palette.divider,
      strokeDashArray: 3,
    },
    colors: [theme.palette.primary.main, theme.palette.secondary.main],
    legend: {
      position: "top" as const,
      labels: { colors: theme.palette.text.primary },
    },
  };

  const budgetVsActualSeries = [
    {
      name: "Actual",
      data: [85000, 92000, 88000, 95000],
    },
    {
      name: "Budget",
      data: [80000, 90000, 85000, 100000],
    },
  ];

  const profitMarginTrendOptions = {
    chart: {
      type: "line" as const,
      toolbar: { show: false },
    },
    stroke: { curve: "smooth" as const, width: 3 },
    xaxis: {
      categories: dashboardData.monthlyTrends.map((t) => t.month),
      labels: { style: { colors: theme.palette.text.secondary } },
    },
    yaxis: {
      labels: {
        style: { colors: theme.palette.text.secondary },
        formatter: (val: number) => `${val.toFixed(1)}%`,
      },
    },
    grid: {
      borderColor: theme.palette.divider,
      strokeDashArray: 3,
    },
    colors: [theme.palette.success.main],
    markers: {
      size: 5,
      strokeWidth: 2,
      strokeColors: "#fff",
    },
  };

  const profitMarginTrendSeries = [
    {
      name: "Profit Margin %",
      data: dashboardData.monthlyTrends.map((t) =>
        t.revenue > 0 ? (t.profit / t.revenue) * 100 : 0
      ),
    },
  ];

  const workingCapitalOptions = {
    chart: {
      type: "area" as const,
      toolbar: { show: false },
    },
    dataLabels: { enabled: false },
    stroke: { curve: "smooth" as const, width: 2 },
    xaxis: {
      categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
      labels: { style: { colors: theme.palette.text.secondary } },
    },
    yaxis: {
      labels: {
        style: { colors: theme.palette.text.secondary },
        formatter: (val: number) => `${(val / 1000).toFixed(0)}K`,
      },
    },
    grid: {
      borderColor: theme.palette.divider,
      strokeDashArray: 3,
    },
    colors: [theme.palette.info.main],
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.3,
        opacityTo: 0.1,
      },
    },
  };

  const workingCapitalSeries = [
    {
      name: "Working Capital",
      data: [45000, 52000, 48000, 55000, 58000, 62000],
    },
  ];

  const handleRefresh = () => {
    // Implement refresh logic
    console.log("Refreshing dashboard data...");
  };

  const handleCustomize = () => {
    setIsCustomizing(!isCustomizing);
  };

  const handlePeriodChange = (period: string) => {
    setSelectedPeriod(period);
    // Implement period change logic
    console.log("Period changed to:", period);
  };

  const getCurrentTimestamp = () => {
    return new Date().toLocaleString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
      {/* Header */}
      <DashboardHeader
        title="Financial Dashboard"
        subtitle="Real-time accounting insights and performance metrics"
        selectedPeriod={selectedPeriod}
        onPeriodChange={handlePeriodChange}
        onRefresh={handleRefresh}
        onCustomize={handleCustomize}
        isCustomizing={isCustomizing}
      />

      {/* Main Content */}
      <Box sx={{ px: { xs: 1, sm: 2 }, pb: 4 }}>
        <Grid container spacing={3}>
          {/* KPI Section - Gauge + 4 Cards */}
          <Grid item xs={12}>
            <Grid container spacing={3}>
              {/* Left - Semi-circular Gauge (1/3 width) */}
              <Grid item xs={12} lg={4}>
                <GaugeChart
                  value={currentMetrics.healthScore}
                  title="Financial Health Score"
                  subtitle="Overall business performance indicator"
                  height={280}
                />
              </Grid>

              {/* Right - 4 KPI Cards in 2x2 Grid (2/3 width) */}
              <Grid item xs={12} lg={8}>
                <Grid container spacing={2} sx={{ height: "100%" }}>
                  <Grid item xs={12} sm={6}>
                    <MetricCard
                      title="Total Revenue"
                      value={currentMetrics.totalRevenue}
                      change={revenueChange}
                      changeLabel="vs last month"
                      icon={<MonetizationOnIcon />}
                      color="primary"
                      format="currency"
                      size="medium"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <MetricCard
                      title="Net Profit"
                      value={currentMetrics.netProfit}
                      change={profitChange}
                      changeLabel="vs last month"
                      icon={<TrendingUpIcon />}
                      color="success"
                      format="currency"
                      size="medium"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <MetricCard
                      title="Total Expenses"
                      value={currentMetrics.totalExpenses}
                      change={expenseChange}
                      changeLabel="vs last month"
                      icon={<ReceiptIcon />}
                      color="warning"
                      format="currency"
                      size="medium"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <MetricCard
                      title="Cash Flow"
                      value={currentMetrics.cashFlow}
                      change={cashFlowChange}
                      changeLabel="vs last month"
                      icon={<AccountBalanceIcon />}
                      color="info"
                      format="currency"
                      size="medium"
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {/* Primary Charts Row */}
          <Grid item xs={12} lg={8}>
            <ChartCard
              title="Revenue & Profit Trends"
              subtitle="7-month performance overview with growth analysis"
              height={420}
              chartOptions={monthlyTrendOptions}
              chartSeries={monthlyTrendSeries}
              chartType="area"
            />
          </Grid>

          <Grid item xs={12} lg={4}>
            <ChartCard
              title="Expense Breakdown"
              subtitle="Current month distribution by category"
              height={420}
              chartOptions={expenseBreakdownOptions}
              chartSeries={expenseBreakdownSeries}
              chartType="donut"
            />
          </Grid>

          {/* Secondary Charts Row */}
          <Grid item xs={12} lg={6}>
            <ChartCard
              title="Budget vs Actual Performance"
              subtitle="Quarterly comparison of planned vs actual results"
              height={380}
              chartOptions={budgetVsActualOptions}
              chartSeries={budgetVsActualSeries}
              chartType="bar"
            />
          </Grid>

          <Grid item xs={12} lg={6}>
            <ChartCard
              title="Profit Margin Trends"
              subtitle="Monthly profitability percentage analysis"
              height={380}
              chartOptions={profitMarginTrendOptions}
              chartSeries={profitMarginTrendSeries}
              chartType="line"
            />
          </Grid>

          {/* Tertiary Charts Row */}
          <Grid item xs={12} lg={7}>
            <ChartCard
              title="Revenue & Profitability Trends"
              subtitle="Dual-axis analysis of revenue growth and profit margins"
              height={380}
              chartOptions={{
                chart: {
                  type: "line",
                  toolbar: { show: false },
                  background: "transparent",
                },
                dataLabels: { enabled: false },
                stroke: {
                  curve: "smooth",
                  width: [3, 3],
                },
                xaxis: {
                  categories: dashboardData.monthlyTrends.map((t) => t.month),
                  labels: {
                    style: {
                      colors: theme.palette.text.secondary,
                      fontSize: "12px",
                    },
                  },
                  axisBorder: { show: false },
                  axisTicks: { show: false },
                },
                yaxis: [
                  {
                    title: {
                      text: "Revenue (BHD)",
                      style: { color: theme.palette.text.secondary },
                    },
                    labels: {
                      style: { colors: theme.palette.text.secondary },
                      formatter: (val: number) => `${(val / 1000).toFixed(0)}K`,
                    },
                  },
                  {
                    opposite: true,
                    title: {
                      text: "Profit Margin (%)",
                      style: { color: theme.palette.text.secondary },
                    },
                    labels: {
                      style: { colors: theme.palette.text.secondary },
                      formatter: (val: number) => `${val.toFixed(1)}%`,
                    },
                  },
                ],
                grid: {
                  borderColor: theme.palette.divider,
                  strokeDashArray: 3,
                  yaxis: { lines: { show: true } },
                  xaxis: { lines: { show: false } },
                },
                colors: [
                  theme.palette.primary.main,
                  theme.palette.success.main,
                ],
                legend: {
                  position: "top",
                  labels: { colors: theme.palette.text.primary },
                },
                tooltip: {
                  theme: theme.palette.mode,
                  shared: true,
                  intersect: false,
                },
              }}
              chartSeries={[
                {
                  name: "Revenue",
                  type: "line",
                  yAxisIndex: 0,
                  data: dashboardData.monthlyTrends.map((t) => t.revenue),
                },
                {
                  name: "Profit Margin",
                  type: "line",
                  yAxisIndex: 1,
                  data: dashboardData.monthlyTrends.map((t) =>
                    t.revenue > 0 ? (t.profit / t.revenue) * 100 : 0
                  ),
                },
              ]}
              chartType="line"
              chartDetails={{
                description:
                  "This advanced dual-axis line chart tracks revenue growth alongside profit margin trends, providing comprehensive insights into business performance efficiency.",
                methodology:
                  "Revenue data is plotted on the primary Y-axis while profit margins are calculated as (Profit/Revenue)*100 and displayed on the secondary Y-axis.",
                dataSource: "Enterprise Financial System (EFS)",
                tableHeaders: [
                  "Month",
                  "Revenue (BHD)",
                  "Profit (BHD)",
                  "Profit Margin (%)",
                ],
                tableData: dashboardData.monthlyTrends.map((t) => [
                  t.month,
                  t.revenue.toLocaleString(),
                  t.profit.toLocaleString(),
                  t.revenue > 0
                    ? ((t.profit / t.revenue) * 100).toFixed(1) + "%"
                    : "0.0%",
                ]),
                insights: [
                  `Average monthly revenue: ${(
                    dashboardData.monthlyTrends.reduce(
                      (sum, t) => sum + t.revenue,
                      0
                    ) / dashboardData.monthlyTrends.length
                  ).toLocaleString()} BHD`,
                  `Highest revenue month: ${
                    dashboardData.monthlyTrends.reduce((max, t) =>
                      t.revenue > max.revenue ? t : max
                    ).month
                  } (${Math.max(
                    ...dashboardData.monthlyTrends.map((t) => t.revenue)
                  ).toLocaleString()} BHD)`,
                  `Average profit margin: ${(
                    dashboardData.monthlyTrends.reduce(
                      (sum, t) =>
                        sum +
                        (t.revenue > 0 ? (t.profit / t.revenue) * 100 : 0),
                      0
                    ) / dashboardData.monthlyTrends.length
                  ).toFixed(1)}%`,
                  `Revenue growth trend: ${(
                    ((dashboardData.monthlyTrends[
                      dashboardData.monthlyTrends.length - 1
                    ].revenue -
                      dashboardData.monthlyTrends[0].revenue) /
                      dashboardData.monthlyTrends[0].revenue) *
                    100
                  ).toFixed(1)}% over period`,
                ],
                chartType: "line",
                totalDataPoints: dashboardData.monthlyTrends.length * 2,
              }}
            />
          </Grid>
          <Grid item xs={12} lg={5}>
            <ChartCard
              title="Monthly Revenue Trend"
              subtitle="Revenue growth pattern over the last 12 months"
              height={360}
              chartOptions={{
                chart: {
                  type: "area",
                  toolbar: { show: false },
                  background: "transparent",
                },
                dataLabels: { enabled: false },
                stroke: {
                  curve: "smooth",
                  width: 3,
                },
                xaxis: {
                  categories: [
                    "Jan",
                    "Feb",
                    "Mar",
                    "Apr",
                    "May",
                    "Jun",
                    "Jul",
                    "Aug",
                    "Sep",
                    "Oct",
                    "Nov",
                    "Dec",
                  ],
                  labels: {
                    style: {
                      colors: theme.palette.text.secondary,
                      fontSize: "11px",
                    },
                  },
                  axisBorder: { show: false },
                  axisTicks: { show: false },
                },
                yaxis: {
                  labels: {
                    style: { colors: theme.palette.text.secondary },
                    formatter: (val: number) => `${(val / 1000).toFixed(0)}K`,
                  },
                },
                grid: {
                  borderColor: theme.palette.divider,
                  strokeDashArray: 3,
                  yaxis: { lines: { show: true } },
                  xaxis: { lines: { show: false } },
                },
                colors: [theme.palette.primary.main],
                fill: {
                  type: "gradient",
                  gradient: {
                    shadeIntensity: 1,
                    opacityFrom: 0.4,
                    opacityTo: 0.1,
                    stops: [0, 100],
                  },
                },
                markers: {
                  size: 4,
                  strokeWidth: 2,
                  strokeColors: "#fff",
                  hover: { size: 6 },
                },
                tooltip: {
                  theme: theme.palette.mode,
                  y: {
                    formatter: (val: number) =>
                      `${(val / 1000).toFixed(0)}K BHD`,
                  },
                },
              }}
              chartSeries={[
                {
                  name: "Revenue",
                  data: [
                    320, 340, 315, 380, 365, 410, 425, 395, 445, 460, 480, 520,
                  ],
                },
              ]}
              chartType="area"
              onDetailsClick={() => console.log("Show revenue trend details")}
            />
          </Grid>
          {/* Additional Analysis Row */}
          <Grid item xs={12} lg={12}>
            <ChartCard
              title="Cash Flow Analysis"
              subtitle="Operating, investing & financing activities breakdown"
              height={360}
              chartOptions={cashFlowOptions}
              chartSeries={cashFlowSeries}
              chartType="line"
            />
          </Grid>
        </Grid>
      </Box>

      {/* Modern Footer */}
      <Box
        component="footer"
        sx={{
          mt: 4,
          px: { xs: 1, sm: 2 },
          pb: 3,
        }}
      >
        <Divider sx={{ mb: 3 }} />
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            gap: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontWeight: 500,
            }}
          >
            © 2024 Financial Dashboard. All rights reserved.
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <ScheduleIcon
              sx={{
                fontSize: "1rem",
                color: "text.disabled",
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: "text.disabled",
                fontSize: "0.75rem",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Last updated: {getCurrentTimestamp()}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
